import { useEffect, useState, ReactNode } from 'react'
import { DarkModeContext, DarkMode } from '../hooks/useDarkMode'

const STORAGE_KEY = 'dark_mode'

function resolveIsDark(mode: DarkMode): boolean {
  if (mode === 'dark') return true
  if (mode === 'light') return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function applyClass(isDark: boolean) {
  if (isDark) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
}

export function DarkModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<DarkMode>(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as DarkMode | null
    return stored && ['light', 'dark', 'system'].includes(stored) ? stored : 'system'
  })

  const [isDark, setIsDark] = useState<boolean>(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as DarkMode | null
    const initialMode = stored && ['light', 'dark', 'system'].includes(stored) ? stored : 'system'
    return resolveIsDark(initialMode)
  })

  // Apply class on mount and whenever mode changes
  useEffect(() => {
    const dark = resolveIsDark(mode)
    setIsDark(dark)
    applyClass(dark)
  }, [mode])

  // Watch system preference changes when in 'system' mode
  useEffect(() => {
    if (mode !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) => {
      setIsDark(e.matches)
      applyClass(e.matches)
    }
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [mode])

  const setMode = (newMode: DarkMode) => {
    localStorage.setItem(STORAGE_KEY, newMode)
    setModeState(newMode)
  }

  const toggle = () => {
    setMode(isDark ? 'light' : 'dark')
  }

  return (
    <DarkModeContext.Provider value={{ mode, isDark, setMode, toggle }}>
      {children}
    </DarkModeContext.Provider>
  )
}
